import React from 'react';
import { OwnerChip, isUncounted, qtyBoxValue } from './lineState';
import { Flags, LineStatus, LocationCell, QtyStepper, RowMenu, rowClassOf } from './LineList';

/*
  COUNTING ON A TABLET, rebrand step 5, 17 September 2026 (brand guide 12.3).

  The lines on the left (name, shelf, item number and the count so far), and on
  the right the one you have chosen: where it is in the list ("3 of 8"), whether
  it is counted, its name large, its shelf, number and vendor, a big counter,
  and Previous and Save and next.

  **The counter is the same box and the same save as everywhere else.** Typing
  saves as each digit goes in (the owner's word of 17 September 2026), and so do
  the big plus and minus. "Save and next" therefore has nothing left to save by
  the time it is pressed; it moves on, and the keys in the box (Enter, the
  arrows, Escape, N) do what they do on every other count box.

  Everything a line's menu offers is on the card: edit, note, the two flags and
  taking it off the sheet, and the shelf when Item Locations is on.
*/
export default function SplitView({
  items, selectedId, onSelect, myId, shared, locationEnabled,
  openMenu, onToggleMenu, actions,
  qtyRef, onQtyFocus, onQtyBlur, onQtyKey, onQty, savedId, invalidId,
  editingLocation, locationDraft, setLocationDraft, onLocationOpen, onLocationSave, onLocationCancel,
  empty,
}) {
  const index = Math.max(0, items.findIndex(i => i.id === selectedId));
  const item = items[index] || null;

  if (!item) return <div className="ce-split-empty">{empty}</div>;

  const go = step => {
    const to = items[index + step];
    if (to) onSelect(to.id, true);
  };
  const label = item.description_raw || item.item_number_raw;

  return (
    <div className="ce-split">
      <div className="ce-split-list" role="listbox" aria-label="Lines on this sheet">
        {items.map(line => {
          const on = line.id === item.id;
          return (
            <button key={line.id} type="button" role="option" aria-selected={on}
              className={'ce-split-row ' + rowClassOf(line) + (on ? ' is-on' : '')}
              onClick={() => onSelect(line.id, false)}>
              <span className="ce-split-row-text">
                <span className="ce-name">{line.description_raw}</span>
                <span className="ce-card-meta">
                  <span className="ce-itemno">{line.item_number_raw}</span>
                  {locationEnabled && line.location && <span>Shelf {line.location}</span>}
                </span>
              </span>
              <span className={'ce-split-row-qty' + (isUncounted(line) ? ' blank' : '')}>
                {isUncounted(line) ? '?' : qtyBoxValue(line)}
              </span>
            </button>
          );
        })}
      </div>

      <div className="ce-detail">
        <div className="ce-detail-top">
          <span className="ce-detail-pos">{(index + 1) + ' of ' + items.length}</span>
          <LineStatus item={item} />
          <span style={{ marginLeft: 'auto' }}>
            <RowMenu item={item} open={openMenu === item.id} onToggle={onToggleMenu} actions={actions} />
          </span>
        </div>
        <h2 className="ce-detail-name">
          {item.description_raw}
          {shared && <OwnerChip item={item} myId={myId} />}
        </h2>
        <div className="ce-detail-facts">
          {locationEnabled && (
            <span>Shelf <LocationCell item={item} editing={editingLocation === item.id} draft={locationDraft}
              setDraft={setLocationDraft} onOpen={onLocationOpen}
              onSave={() => onLocationSave(item)} onCancel={onLocationCancel} /></span>
          )}
          <span className="ce-itemno" style={{ color: item.not_in_catalog ? 'var(--warning-ink)' : 'var(--action-text)' }}>
            {item.item_number_raw}<Flags item={item} />
          </span>
          <span>{item.vendor_raw || '--'}</span>
        </div>
        {item.notes && <div className="ce-note">Note: {item.notes}</div>}

        <div className="ce-detail-counter">
          <QtyStepper item={item} big
            inputRef={el => qtyRef(item.id, 'detail', el)}
            onFocus={e => onQtyFocus(item.id, e)}
            onBlur={() => onQtyBlur(item.id)}
            onKeyDown={e => onQtyKey(e, item)}
            onChange={v => onQty(item.id, v)}
            saved={savedId === item.id}
            invalid={invalidId === item.id}
            label={label} />
        </div>

        <div className="ce-detail-nav">
          <button type="button" className="btn btn-outline" disabled={index === 0} onClick={() => go(-1)}>Previous</button>
          <button type="button" className="btn btn-primary" disabled={index >= items.length - 1} onClick={() => go(1)}>Save and next</button>
        </div>
      </div>
    </div>
  );
}
